// Where the sign-in lives between visits.
//
// The access token has always been kept in sessionStorage, which dies with
// the tab: a refresh, a closed window, or the OS reclaiming a backgrounded
// tab means logging in again. The backend has issued a refresh token on
// every login since the start and neither app has ever used it.
//
// "Keep me signed in" is the choice: ticked, the refresh token goes to
// localStorage and the session is rebuilt on the next visit; unticked — the
// default — nothing durable is written and the old behaviour stands. On a
// phone that belongs to one person it will usually be ticked; the default
// stays off so it is always a decision.
//
// The access token stays in sessionStorage as before, so every existing
// caller keeps reading it from where it always was.

import { SERVER_URL } from "./constants.js";

const KEEP_KEY = "keepSignedIn";
const REFRESH_KEY = "refreshToken";
// Shared across tabs so a second tab reuses the token rather than racing to
// rotate it — the server retires the old pair, which would sign the first
// tab out.
const ACCESS_KEY = "accessToken";
// Who was signed in, and what the server last said they may do.
//
// The role decides which tiles are drawn, nothing more: the money,
// transport and supervision endpoints are gated server-side, and authorities
// are read from the user row on every request. It is worth strictly less
// than the refresh token sitting beside it, and is replaced from the server
// on every renewal.
const EMAIL_KEY = "sessionEmail";
const ROLE_KEY = "sessionRole";
const GRANTS_KEY = "sessionGrants";

// Renew this long before the token runs out, rather than on the failure.
const RENEW_WITHIN_MS = 5 * 60 * 1000;
// How often an open tab looks at its own token. Cheap: it is arithmetic on a
// claim, and only a token near its end causes a request.
const CHECK_EVERY_MS = 60 * 1000;

/** The `exp` claim in milliseconds, or null if the token is unreadable. */
/** The `exp` claim in milliseconds, or null if the token is unreadable. */
export function tokenExpiry(token) {
  try {
    const [, payload] = String(token || "").split(".");
    if (!payload) return null;
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
          .split("")
          .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join("")
      )
    );
    return typeof json?.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True when the token is missing, unreadable, spent, or nearly so. */
export function needsRenewal(token, now = Date.now()) {
  const exp = tokenExpiry(token);
  if (exp == null) return true;
  return exp - now <= RENEW_WITHIN_MS;
}

export function isDurable() {
  try {
    return localStorage.getItem(KEEP_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Remember this sign-in. `keepSignedIn` decides whether anything outlives the
 * tab; without it the durable keys are cleared, so unticking it on the next
 * login undoes a previous choice.
 */
export function startSession({ accessToken, refreshToken, keepSignedIn, email, role, grants }) {
  sessionStorage.setItem("bearer", accessToken || "");
  sessionStorage.setItem("isAuthenticated", "true");
  try {
    if (keepSignedIn) {
      localStorage.setItem(KEEP_KEY, "1");
      localStorage.setItem(REFRESH_KEY, refreshToken || "");
      localStorage.setItem(ACCESS_KEY, accessToken || "");
      if (email) localStorage.setItem(EMAIL_KEY, email);
      if (role) localStorage.setItem(ROLE_KEY, role);
      if (grants) localStorage.setItem(GRANTS_KEY, JSON.stringify(grants));
    } else {
      clearDurable();
    }
  } catch {
    // Private browsing, or storage the user has blocked. The session still
    // works for this tab; it simply will not outlive it.
  }
}

function durable(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * True when the durable session belongs to someone other than whoever this
 * tab is signed in as. A tab with no identity of its own — a fresh one
 * rebuilding a session — is nobody yet, and adopts what it finds.
 */
export function isAnotherUsersSession() {
  const mine = (sessionStorage.getItem("userEmail") || "").toLowerCase();
  const stored = durableEmail().toLowerCase();
  return Boolean(mine) && Boolean(stored) && mine !== stored;
}

/** The email of the stored session, for the caller to re-announce on restore. */
export function durableEmail() {
  return durable(EMAIL_KEY) || "";
}

export function clearDurable() {
  try {
    localStorage.removeItem(KEEP_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(EMAIL_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(GRANTS_KEY);
  } catch {
    /* nothing to clear */
  }
}

/**
 * Rebuild the session on a fresh page, if there is one to rebuild. Returns
 * the login response so the caller can set role and grants from the server
 * rather than from anything stored: authority is re-derived every time, never
 * restored from the browser.
 *
 * A valid access token is reused as-is, so opening a second tab does not
 * rotate the pair out from under the first. Anything else — expired, nearly
 * expired, unreadable — is exchanged for a new pair.
 */
// One restore per page, shared by every caller. React mounts an effect twice
// in development, and without this the second call presents a refresh token
// the first has already rotated away: the server refuses it, the session is
// cleared, and the first result is thrown away as a cancelled effect. The
// user is left at the login form holding a perfectly good session.
let inFlight = null;
// One renewal at a time per tab: the timer, a returning tab and a boot
// restore can all want one at once, and the second would present a token the
// first has already rotated away.
let renewing = null;

export function restoreSession() {
  if (!inFlight) inFlight = doRestore();
  return inFlight;
}

/** Test seam: forget the shared restore between cases. */
export function resetRestoreForTests() {
  inFlight = null;
}

async function doRestore() {
  if (!isDurable()) return null;

  const refreshToken = durable(REFRESH_KEY);
  const accessToken = durable(ACCESS_KEY);
  if (!refreshToken) {
    clearDurable();
    return null;
  }

  // A token with hours left is used as it stands. Renewing it would retire
  // the pair this session holds, and any other tab still using the old access
  // token would start being refused — opening a second tab must not sign the
  // first one out.
  if (accessToken && !needsRenewal(accessToken)) {
    sessionStorage.setItem("bearer", accessToken);
    sessionStorage.setItem("isAuthenticated", "true");
    let grants = [];
    try {
      const stored = JSON.parse(durable(GRANTS_KEY) || "[]");
      grants = Array.isArray(stored) ? stored : [];
    } catch {
      grants = [];
    }
    return {
      access_token: accessToken,
      role: durable(ROLE_KEY) || null,
      grants,
      reused: true,
    };
  }

  return renewNow();
}

/**
 * Exchange the stored refresh token for a fresh pair and keep it. Returns the
 * login response, or null: `expired` distinguishes "the session is over" —
 * revoked, expired, disabled, or past the age its client allows — from a
 * renewal that merely failed to happen, so a flaky network never signs
 * anyone out.
 */
export async function renewNow() {
  const refreshToken = durable(REFRESH_KEY);
  if (!refreshToken) return null;

  // The durable session is shared between tabs; this tab's identity is not.
  // If someone has signed in as a different person in another tab, the token
  // sitting in storage is theirs, and adopting it would leave this tab
  // showing one person's screens while acting as another.
  if (isAnotherUsersSession()) return { otherUser: true };

  if (renewing) return renewing;
  renewing = (async () => {
    try {
      let res = await renew(refreshToken);

      // 403 means revoked, expired, disabled, or past the session cap.
      // Before believing it, check whether another tab rotated the pair
      // while this request was in the air — the token we sent would then be
      // spent through no fault of the session, and the one now stored is
      // good.
      if (res.status === 403) {
        const current = durable(REFRESH_KEY);
        if (current && current !== refreshToken) {
          res = await renew(current);
        }
      }

      // Still refused: the session really is over and the stored token is
      // worthless. Anything else (offline, a 500) leaves it alone.
      if (res.status === 403) {
        clearDurable();
        return { expired: true };
      }
      if (!res.ok) return null;

      const data = await res.json();
      if (!data?.access_token) return null;

      startSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token || refreshToken,
        keepSignedIn: true,
        email: durableEmail(),
        role: data.role,
        grants: data.grants,
      });
      return data;
    } catch {
      return null;
    } finally {
      renewing = null;
    }
  })();
  return renewing;
}

/**
 * Renew before the access token runs out, rather than after.
 *
 * Renewal used to happen only when the page loaded, so a tab left open past
 * the access token's life sat on a dead credential: the screens stayed up
 * and every request was refused, which reads as a broken app rather than a
 * session that ended. A tab in use now keeps itself current, and one that
 * cannot — because the session reached the age its client allows — says so
 * through `onLost` instead of going quiet.
 *
 * Checks on a timer and whenever the tab is brought back, which is when a
 * laptop reopened after lunch catches up. Returns its own cleanup.
 */
export function watchSession(onLost) {
  const check = async () => {
    if (!isDurable()) return;
    if (isAnotherUsersSession()) {
      onLost?.();
      return;
    }
    const token = sessionStorage.getItem("bearer") || durable(ACCESS_KEY);
    if (!needsRenewal(token)) return;
    const result = await renewNow();
    if (result?.expired || result?.otherUser) onLost?.();
  };

  // Another tab signing out, or signing in as someone else, reaches this one
  // through storage. Logging out revokes the session for every tab sharing
  // it, so carrying on here would only mean failing requests; and a session
  // belonging to someone else is never this tab's to use.
  let wasDurable = isDurable();
  const onStorage = (event) => {
    if (event.storageArea !== localStorage) return;
    if (![KEEP_KEY, REFRESH_KEY, EMAIL_KEY, null].includes(event.key)) return;
    if (isAnotherUsersSession() || (wasDurable && !isDurable())) onLost?.();
    wasDurable = isDurable();
  };
  window.addEventListener("storage", onStorage);

  const onVisible = () => {
    if (document.visibilityState === "visible") check();
  };

  const timer = setInterval(check, CHECK_EVERY_MS);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("focus", check);

  return () => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("focus", check);
    window.removeEventListener("storage", onStorage);
  };
}

function renew(token) {
  return fetch(`${SERVER_URL}api/v1/auth/refresh-token`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ---------------------------------------------------------------------------
// Refused requests
// ---------------------------------------------------------------------------

// A screen full of gated requests must not renew once per failure, and the
// guard against that has to be narrow: anything skipped here is a revoked
// session going unnoticed for that much longer.
//
// One guard is enough — the endpoints already proved gated, where renewal
// worked and the retry was refused anyway. That is a property of the
// endpoint rather than of the moment, so it never blinds us to a session
// ending. A burst of parallel refusals needs no guard of its own: renewal is
// single-flight, so they all wait on the same one request.
const gatedPaths = new Set();
let interceptorInstalled = false;

const AUTH_PATHS = ["/auth/authenticate", "/auth/refresh-token", "/auth/logout"];

function isOurApi(url) {
  const value = String(url || "");
  // Absolute against the configured backend, or a relative /api/… path —
  // some call sites write one and some the other, and both are ours. A
  // third party's URL is not, whatever it contains.
  if (SERVER_URL) return value.startsWith(SERVER_URL) || value.startsWith("/api/");
  // Only with no backend configured, which is the test environment.
  return value.includes("/api/");
}

/**
 * Tell a session that has ended from a request that was never allowed.
 *
 * Both arrive as a 403, and the difference matters: the first should return
 * someone to the login form, the second is the FINANCE or TRANSPORT gate
 * doing its job and must leave them where they are. Rather than reading the
 * shape of the body, ask the server the only question that settles it — can
 * this session still be renewed?
 *
 *   renewal refused        the session is over          → sign out
 *   renewal fine, retry ok  the token was stale          → nobody notices
 *   renewal fine, still 403 not for you                  → the caller's error
 *
 * It costs one extra request on a genuine refusal, which is rare: the front
 * end hides the controls it knows are gated, so a 403 usually means a URL
 * typed by hand or a grant taken away mid-session.
 *
 * Patches fetch because there are 274 call sites across 88 files and six
 * wrappers; one place that every request already passes through beats
 * touching all of them. Only requests to our own API are considered, and the
 * authentication endpoints are left alone — they answer for themselves.
 */
export function installAuthInterceptor(onLost) {
  if (interceptorInstalled || typeof window === "undefined") return () => {};
  interceptorInstalled = true;

  const original = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const res = await original(input, init);

    const url = typeof input === "string" ? input : input?.url;
    if ((res.status !== 401 && res.status !== 403) || !isOurApi(url)) return res;
    if (AUTH_PATHS.some((p) => String(url).includes(p))) return res;
    if (init?.__retried) return res;

    const path = pathOf(url);
    const token = sessionStorage.getItem("bearer");
    const stale = needsRenewal(token);
    if (!stale && gatedPaths.has(path)) return res;

    const renewed = await renewNow();

    if (renewed?.expired || renewed?.otherUser) {
      onLost?.();
      return res;
    }
    if (!renewed?.access_token) return res;

    // Renewal worked, so the session is alive and the refusal was either a
    // stale token or a real lack of authority. One retry tells us which, and
    // whichever it is, the caller gets a straight answer.
    const headers = new Headers(init?.headers || (typeof input !== "string" ? input.headers : undefined));
    headers.set("Authorization", `Bearer ${renewed.access_token}`);
    const retried = await original(input, { ...(init || {}), headers, __retried: true });

    // Refused again on a session that is demonstrably alive: this endpoint is
    // not for this account. Remember it, so the next refusal from it costs
    // nothing and never delays noticing a session that really has ended.
    if (retried.status === 401 || retried.status === 403) gatedPaths.add(path);
    return retried;
  };

  return () => {
    window.fetch = original;
    interceptorInstalled = false;
  };
}

function pathOf(url) {
  const value = String(url || "");
  const withoutQuery = value.split("?")[0];
  const apiAt = withoutQuery.indexOf("/api/");
  return apiAt >= 0 ? withoutQuery.slice(apiAt) : withoutQuery;
}

/** Test seam: forget that fetch was ever patched, and what it learned. */
export function resetInterceptorForTests() {
  interceptorInstalled = false;
  gatedPaths.clear();
}
